import * as React from 'react';
import styled, { css } from 'styled-components'

import { Dropdown } from '@longline/aqua-ui/inputs/Dropdown';
import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { ListView } from '@longline/aqua-ui/controls/ListView/ListView';

import { TCellValue } from '../../util/TCellValue';
import { ExcelColumn, ExcelColumnDictionary } from './IExcelColumn';

interface IProps {
  /** @ignore */
  children?: React.ReactNode;

  columns?: ExcelColumn[];
  /** Number of rows to grey out (from the top): */
  skiprows: number;
  /** Data to display. */
  data: TCellValue[][];
  /** Total rows in worksheet */
  rowCount: number;
  /** Is data currently loading? */
  loading: boolean;
  // Column indices for each importable variable. null means "not assigned".
  longitude: number | null;
  latitude: number | null;
  depth: number | null;
  eastspeed: number | null;
  northspeed: number | null;
  yearly_poc: number | null;
  daily_poc: number | null;
  oxygen: number | null;
  ammonia: number | null;
  chlorophyll: number | null;
  pathogen: number | null;
  pocInSediment: number | null;
  pomInSediment: number | null;
  bacteriaInSediment: number | null;
  oxygenInSediment: number | null;
  sulphateInSediment: number | null;
  ironHydroxideInSediment: number | null;
  totalSulphideInSediment: number | null;
  ironInSediment: number | null;
  ironSulphideInSediment: number | null;
  therapeutants: number | null;

  /** Called when a data source is assigned to a column. */
  onSetColumn: (idx: number, type: ExcelColumn) => void;
}

/**
 * ExcelTable displays table content with Excel-like styling.
 */

const ExcelTable = (props: IProps) => {

  const formatValue = (value: TCellValue): any => {
    if(value == null) return null;
    switch(typeof(value)) {
      case 'number':
        return <NumberFormatter value={value} decimals={4}/>;
      case "string":
        return <span title={value}>{value}</span>;
      case "object":
        return value;
      case "boolean":
        return value;
      default:
        return "other";
    }
  }

  const getDropValue = (idx: number) => {
    switch(idx) {
      case props.latitude: return 'latitude';
      case props.longitude: return 'longitude';
      case props.depth: return 'depth';
      case props.eastspeed: return 'u';
      case props.northspeed: return 'v';
      case props.daily_poc: return 'daily_poc';
      case props.yearly_poc: return 'yearly_poc';
      case props.oxygen: return 'oxygen';
      case props.ammonia: return 'ammonia';
      case props.chlorophyll: return 'chlorophyll';
      case props.pathogen: return 'pathogen';
      case props.pocInSediment: return 'poc_in_sediment';
      case props.pomInSediment: return 'pom_in_sediment';
      case props.bacteriaInSediment: return 'bacteria_sediment';
      case props.oxygenInSediment: return 'oxygen_in_sediment';
      case props.sulphateInSediment: return 'sulphate_in_sediment';
      case props.ironHydroxideInSediment: return 'iron_hydroxide_in_sediment';
      case props.totalSulphideInSediment: return 'total_sulphide_in_sediment';
      case props.ironInSediment: return 'iron_in_sediment';
      case props.ironSulphideInSediment: return 'iron_sulphide_in_sediment';
      case props.therapeutants: return 'therapeutants';
      default: return null;
    }
  }

  const getSelectedColumns = () => {
    return [props.latitude, props.longitude, props.depth, props.eastspeed, props.northspeed,
            props.yearly_poc, props.daily_poc, props.oxygen, props.ammonia, props.chlorophyll ].filter(n => n != null);
  }

  const getDropdown = (index: number) => {
    const options = props.columns ?? 
    [
      'latitude',
      'longitude',
      'depth',
      'u',
      'v',
      'daily_poc',
      'yearly_poc',
      'oxygen',
      'ammonia',
      'chlorophyll',
      'pathogen',
      'poc_in_sediment',
      'pom_in_sediment',
      'bacteria_sediment',
      'oxygen_in_sediment',
      'sulphate_in_sediment',
      'iron_hydroxide_in_sediment',
      'total_sulphide_in_sediment',
      'iron_in_sediment',
      'iron_sulphide_in_sediment',
      'therapeutants'
    ];

    return (
      <DropdownWrapper key={index} $cols={props.data[0].length}>
        <Dropdown 
          clearable 
          fluid 
          minBodyWidth={200}
          placeholder="Select data type" 
          data={options} 
          label={(item) => ExcelColumnDictionary[item].label}
          value={getDropValue(index)}
          onChange={(value: string) => props.onSetColumn(index, value as ExcelColumn)}
        >
          <Dropdown.Column>{(item) => ExcelColumnDictionary[item].label}</Dropdown.Column>
        </Dropdown>
      </DropdownWrapper>
    );
  }

  // If data is not available, render a notice.
  if(props.loading == true || props.data.length == 0) {
    return (
      <TableWrapper key="ghosted">
        <ListView grid ghost dark shadow data={props.data}>
          {[0,1,2,3,4].map((_, index) => 
            <ListView.Column key={index} active name={index.toString()}>{(item) => ""}</ListView.Column>
          )}
        </ListView>
        {props.data.length == 0 && <NoData>
          This worksheet contains no data.
        </NoData>}
      </TableWrapper>
    );
  }  

  return (
    <>
      <DropdownRow>
        {props.data[0].map((d, index) => getDropdown(index))}
      </DropdownRow>
      <TableWrapper $skiprows={props.skiprows} $selected={[...getSelectedColumns()]} key="realthing">
        <ListView grid dark noheader data={props.data}>
          {props.data[0].map((d, index) => 
            <ListView.Column key={index} active name={index.toString()}>
              {(item) => item[index] == null ? null : formatValue(item[index])}
            </ListView.Column>
          )}
        </ListView>
      </TableWrapper>
      <Counter>
        {props.data.length == 0 ? "(no data)" : <>Top 20 rows of <NumberFormatter value={props.rowCount} decimals={0}/></>}
      </Counter>
    </>
  );
}

const DropdownRow = styled.div`
  display: flex;
  background: ${p => p.theme.colors.neutral[10]};
  height: 48px;
  align-items: center;
`

const DropdownWrapper = styled.div<{ $cols: number }>`
  flex: 1;
  box-sizing: border-box;
  padding-left: 4px;
  padding-right: 4px;
  max-width: ${p => `${(100 / p.$cols)}%`};
`

const TableWrapper = styled.div<{ $skiprows?: number, $selected?: number[] }>`
  flex: 1;
  border-bottom: solid 1px #333;
  ${p => (p.$skiprows ?? 0) > 0 && css`
    tr:nth-child(-n+${p.$skiprows}) {
      td {
        background-color: ${p.theme.colors.primary[4]};
        color: ${p.theme.colors.primary[2]};
      }
    }
  `}

  ${p => p.$selected && p.$selected.map(n => css`
    td:nth-child(${n+1}) {
      background-color: ${p.theme.colors.primary[1]};
    }
  `)}
`

const NoData = styled.div`
  position: absolute;
  z-index: 10;
  top: 100px;
  left: 50%;
  translate: -50% 0;
  display: flex;
  align-items: center;
  background: ${p => p.theme.colors.primary[2]};
  height: 28px;
  border-radius: 14px;
  padding-left: 12px;
  padding-right: 12px;
  margin: 20px;
  box-shadow: 1px 1px 1px rgba(0,0,0,0.3);
`

const Counter = styled.div`
  position: absolute;
  z-index: 10;
  right: 8px;
  bottom: 128px;
  background: ${p => p.theme.colors.primary[2]};
  height: 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 80%;
  box-shadow: 1px 1px 1px rgba(0,0,0,0.3);
`

export { ExcelTable }